import type { Category } from "../../src/lib/catalog-copy.ts";
import { colorCountText, FILTER_AT, filterCountText, fromLine, groupCategories, matchesColor } from "./catalog.ts";
import type { StyleSummary } from "./catalog.ts";
import { h } from "./dom.ts";

// Step 1 on the designer and the Studio: category chips, then the category's style cards, then the picked style's
// color swatches (the 160 px thumbs, loaded lazily), with a "Find a color" box on a style with many colors.

export interface PickerColor {
  id: string;
  name: string;
  color: string;
  thumbUrl: string;
}

export interface PickerView<G extends PickerColor> {
  styles: StyleSummary[];
  // The category shown (shownCategory) and the style picked.
  category: Category | null;
  style: string | null;
  // The picked style's colors, null while they load.
  colors: G[] | null;
  picked: string[];
  filter: string;
  locked: boolean;
  colorLabel: (g: G) => string;
  onCategory: (c: Category) => void;
  onStyle: (key: string) => void;
  onToggle: (g: G, input: HTMLInputElement) => void;
  onFilter: (text: string) => void;
  // A swatch link expired (the page sat open past the signed links' life): the page fetches the style again.
  onThumbError?: () => void;
  // The same for a style card's swatch (the catalog's links).
  onCardError?: () => void;
}

// Hides the swatches the filter leaves out, in place, so the box keeps focus while typing. A picked color always
// stays, so it can be unpicked.
export function applyColorFilter(root: ParentNode, query: string): void {
  const cards = [...root.querySelectorAll<HTMLElement>("#swatches [data-color]")];
  let shown = 0;
  for (const card of cards) {
    const on = matchesColor(card.dataset.color ?? "", query) || card.classList.contains("on");
    card.hidden = !on;
    if (on) shown++;
  }
  const count = root.querySelector("#filter-count");
  if (count) count.textContent = filterCountText(shown, cards.length, query);
}

// mount() rebuilds the chip row, and a new row starts scrolled to its left end: on a phone the chip just tapped would
// slide out of sight. Call after every mount: it scrolls the row (never the page) so the chosen chip sits in the middle,
// or as near as the row's ends allow.
export function revealChosenChip(root: ParentNode): void {
  const row = root.querySelector<HTMLElement>("#categories");
  const chip = row?.querySelector<HTMLElement>(".chip.on");
  if (!row || !chip) return;
  const r = row.getBoundingClientRect();
  const c = chip.getBoundingClientRect();
  if (r.width === 0) return;
  row.scrollLeft += c.left - r.left - (r.width - c.width) / 2;
}

export function stylePicker<G extends PickerColor>(v: PickerView<G>): (Node | false | null | undefined)[] {
  const groups = groupCategories(v.styles);
  const group = groups.find((g) => g.key === v.category);
  const style = v.styles.find((s) => s.key === v.style);
  const chips = groups.length > 1 && h("div", { id: "categories", class: "row scroll", role: "group", "aria-label": "Category" }, ...groups.map((g) =>
    h("button", { id: `cat-${g.key}`, type: "button", class: `chip${g.key === v.category ? " on" : ""}`, "aria-pressed": String(g.key === v.category),
      disabled: v.locked, onclick: () => v.onCategory(g.key) }, g.label)));
  const cards = group && h("div", { class: "styles" }, ...group.styles.map((s) =>
    h("button", { id: `style-${s.key}`, type: "button", class: `card style-card${s.key === v.style ? " on" : ""}`, "aria-pressed": String(s.key === v.style),
      disabled: v.locked, onclick: () => v.onStyle(s.key) },
      s.swatch ? h("img", { src: s.swatch.thumbUrl, alt: "", width: 160, height: 200, loading: "lazy", decoding: "async", onerror: () => v.onCardError?.() }) : h("span", { class: "noswatch" }),
      h("b", {}, s.name),
      s.description && h("span", {}, s.description),
      h("span", { class: "muted" }, colorCountText(s.colorCount)),
      s.from && h("span", { class: "from" }, fromLine(s.from)))));
  if (!style) return [chips, cards];

  const colors = v.colors;
  const many = (colors?.length ?? 0) > FILTER_AT;
  const filterBox = many && h("label", { class: "field filter" },
    h("span", {}, "Find a color"),
    h("input", { id: "color-filter", type: "search", autocomplete: "off", placeholder: "Navy, heather, red...", value: v.filter, "aria-describedby": "filter-count",
      oninput: (e: Event) => {
        const text = (e.target as HTMLInputElement).value;
        v.onFilter(text);
        applyColorFilter(document, text);
      } }),
    h("span", { id: "filter-count", class: "muted", "aria-live": "polite" }));
  const swatches = colors && h("div", { id: "swatches", class: "grid swatches", role: "group", "aria-labelledby": "swatches-title" }, ...colors.map((g) => {
    const on = v.picked.includes(g.id);
    return h("label", { class: `card pick${on ? " on" : ""}`, "data-color": g.color },
      h("input", { id: `color-${g.id}`, type: "checkbox", "aria-label": v.colorLabel(g), checked: on, disabled: v.locked, onchange: (e: Event) => v.onToggle(g, e.target as HTMLInputElement) }),
      h("img", { src: g.thumbUrl, alt: "", width: 160, height: 200, loading: "lazy", decoding: "async", onerror: () => v.onThumbError?.() }),
      h("span", {}, v.colorLabel(g)));
  }));
  return [chips, cards,
    h("h3", { id: "swatches-title", class: "swatch-title", tabIndex: -1 }, `${style.name}: ${colorCountText(colors?.length ?? style.colorCount)}`),
    filterBox,
    colors ? swatches : h("p", { class: "muted" }, "Loading colors...")];
}
